import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authBackend } from "./authBackend";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API });

// Refresh a few seconds before expiry so refresh-after-reload doesn't 401.
const EXPIRY_SKEW_SEC = 30;

http.interceptors.request.use(async (config) => {
  let { data } = await authBackend.getSession();
  let session = data?.session;

  const expiresAt = session?.expires_at;
  if (session && typeof expiresAt === "number") {
    const nowSec = Math.floor(Date.now() / 1000);
    if (expiresAt <= nowSec + EXPIRY_SKEW_SEC) {
      const refreshed = await authBackend.refreshSession();
      session = refreshed.data?.session || session;
    }
  }

  const token = session?.access_token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export async function fetchMe() {
  return (await http.get("/auth/me")).data;
}

export async function signup(email, password) {
  return (await http.post("/auth/signup", { email, password })).data;
}

// Used to be a safety net behind Supabase Realtime's push-driven cache
// invalidation (lib/useRealtimeSync.js) -- Realtime is gone with the rest
// of Supabase (see that file), so this interval is the only thing keeping
// these queries current until it's replaced with a real push mechanism.
// const REALTIME_FALLBACK_POLL_MS = 15_000;

// The queries below are kept current by push, not by polling: every change to
// their tables reaches the browser as a Postgres NOTIFY over /api/events, and
// lib/useRealtimeSync.js refetches exactly the queries it touches (and every
// one of them after a reconnect, in case something was missed). So they do
// not refetch on window focus or on remount: with dozens of conversation cards
// each holding a few of these, that alone fired hundreds of requests, which
// queued behind the browser's per-host connection limit and delayed the
// requests that mattered. The stale time only bounds how long an unwatched
// cache entry is trusted if a change notice were ever lost.
const LIVE_STALE_MS = 5 * 60_000;

export function useGroups(options = {}) {
  return useQuery({
    queryKey: ["groups"],
    queryFn: async () => (await http.get("/groups")).data,
    staleTime: LIVE_STALE_MS,
    // refetchInterval: options.refetchInterval ?? REALTIME_FALLBACK_POLL_MS,
    refetchOnWindowFocus: false,
    enabled: options.enabled ?? true,
  });
}

// Ids of the groups with a customer message still waiting for a reply, for the
// desk's Unanswered Messages queue. Re-checked every half minute: nothing pushes
// this one to the browser.
export function useAwaitingReply(options = {}) {
  return useQuery({
    queryKey: ["groups", "awaiting-reply"],
    queryFn: async () => (await http.get("/groups/awaiting-reply")).data,
    staleTime: 15_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    enabled: options.enabled ?? true,
  });
}

export function useStakeholders(workshopLocation) {
  return useQuery({
    queryKey: ["stakeholders", workshopLocation],
    // Goes through `http`, not bare fetch: this endpoint is brand-scoped now,
    // so the request has to carry the session token like every other one.
    queryFn: async () =>
      (await http.get(`/stakeholders/${encodeURIComponent(workshopLocation)}`)).data,
    enabled: !!workshopLocation,
    staleTime: LIVE_STALE_MS,
    // refetchInterval: REALTIME_FALLBACK_POLL_MS,
    refetchOnWindowFocus: false,
  });
}

export function useSentimentHistory(row) {
  return useQuery({
    queryKey: ["sentiment-history", row],
    queryFn: async () => (await http.get(`/groups/${row}/sentiment-history`)).data,
    enabled: !!row,
    staleTime: LIVE_STALE_MS,
    // refetchInterval: REALTIME_FALLBACK_POLL_MS,
    refetchOnWindowFocus: false,
  });
}

export function useAllSentimentHistory() {
  return useQuery({
    queryKey: ["sentiment-history", "all"],
    queryFn: async () => (await http.get("/sentiment-history")).data,
    staleTime: LIVE_STALE_MS,
    // refetchInterval: REALTIME_FALLBACK_POLL_MS,
    refetchOnWindowFocus: false,
  });
}

export function useAllStaffToneFlags() {
  return useQuery({
    queryKey: ["staff-tone-flags", "all"],
    queryFn: async () => (await http.get("/staff-tone-flags")).data,
    staleTime: LIVE_STALE_MS,
    refetchOnWindowFocus: false,
  });
}

export function useAllMessageLog() {
  return useQuery({
    queryKey: ["message-log", "all"],
    queryFn: async () => (await http.get("/message-log")).data,
    staleTime: LIVE_STALE_MS,
    // refetchInterval: REALTIME_FALLBACK_POLL_MS,
    refetchOnWindowFocus: false,
  });
}

export function useIntakeRecord(token) {
  return useQuery({
    queryKey: ["intake", token],
    queryFn: async () => (await http.get(`/intake/${token}`)).data,
    enabled: !!token,
    retry: false, // a 404 here is a real, expected state (invalid/expired/consumed) - not worth retrying
    staleTime: Infinity,
  });
}

export function useOpenIntakeRecords() {
  return useQuery({
    queryKey: ["intake", "open"],
    queryFn: async () => (await http.get("/intake")).data,
    staleTime: LIVE_STALE_MS,
  });
}

// One read of the groups list, outside React Query's own refetching: the booking
// flow polls it while a new group is created. The caller puts the result in the
// ["groups"] cache so the desk already has the row when the advisor gets there.
export async function fetchGroupsList() {
  return (await http.get("/groups")).data;
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await http.post("/groups", payload)).data,
    onSuccess: (data) => qc.setQueryData(["groups"], data),
  });
}

export function useConfig() {
  return useQuery({
    queryKey: ["config"],
    queryFn: async () => (await http.get("/config")).data,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useFeedback() {
  return useQuery({
    queryKey: ["feedback"],
    queryFn: async () => (await http.get("/feedback")).data,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useRegions() {
  return useQuery({
    queryKey: ["regions"],
    queryFn: async () => (await http.get("/regions")).data,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

export function useCreateRegion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await http.post("/regions", payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["regions"] }),
  });
}

export function useDeleteRegion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (regionId) => (await http.delete(`/regions/${regionId}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["regions"] }),
  });
}

export function useDealers(regionId) {
  return useQuery({
    queryKey: ["dealers", regionId ?? "all"],
    queryFn: async () =>
      (await http.get("/dealers", { params: regionId ? { region_id: regionId } : {} })).data,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

export function useCreateDealer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await http.post("/dealers", payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dealers"] }),
  });
}

export function useAddDealerRegion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ dealerId, regionId }) =>
      (await http.post(`/dealers/${dealerId}/regions`, { region_id: regionId })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dealers"] }),
  });
}

export function useWorkshopServiceAdvisors() {
  return useQuery({
    queryKey: ["workshop-service-advisors"],
    queryFn: async () => (await http.get("/workshop-service-advisors")).data,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useUploadStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ row, stage, files, notes }) => {
      const fd = new FormData();
      fd.append("stage", stage);
      (files || []).forEach((f) => fd.append("files", f));
      if (notes != null) fd.append("notes", notes);
      return (
        await http.post(`/groups/${row}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        })
      ).data;
    },
    onSuccess: (data) => qc.setQueryData(["groups"], { headers: data.headers, rows: data.rows }),
  });
}

export function useUpdateGroupDetails() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ row, ...payload }) => (await http.patch(`/groups/${row}`, payload)).data,
    onSuccess: (data) => qc.setQueryData(["groups"], data),
  });
}

export function useSetDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ row, option }) => (await http.post(`/groups/${row}/delivery`, { option })).data,
    onSuccess: (data) => qc.setQueryData(["groups"], data),
  });
}

export function useSendFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ row }) => (await http.post(`/groups/${row}/feedback/send`)).data,
    onSuccess: (data) => qc.setQueryData(["groups"], data),
  });
}

export function useRetryGroupSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row) => (await http.post(`/groups/${row}/retry-sync`)).data,
    onSuccess: (data) => qc.setQueryData(["groups"], data),
  });
}

export function useSendAppDownload() {
  return useMutation({
    mutationFn: async ({ row }) => (await http.post(`/groups/${row}/app-download/send`)).data,
  });
}

export function useGroupMessageLog(row) {
  return useQuery({
    queryKey: ["message-log", "group", row],
    queryFn: async () => (await http.get(`/groups/${row}/message-log`)).data,
    enabled: !!row,
    staleTime: LIVE_STALE_MS,
    // refetchInterval: 15_000,
    refetchOnWindowFocus: false,
  });
}

export function useGroupEvents(row) {
  return useQuery({
    queryKey: ["group-events", row],
    queryFn: async () => (await http.get(`/groups/${row}/events`)).data,
    enabled: !!row,
    staleTime: LIVE_STALE_MS,
    // refetchInterval: 15_000,
    refetchOnWindowFocus: false,
  });
}

// The group files page the customer opens. Public: no session, so it goes
// around the authenticated client above.
export async function fetchPublicGroupFiles(token) {
  return (await axios.get(`${API}/public/group-files/${encodeURIComponent(token)}`)).data;
}

export function useUploadChatAttachments() {
  return useMutation({
    mutationFn: async ({ row, files }) => {
      const fd = new FormData();
      (files || []).forEach((f) => fd.append("files", f));
      return (
        await http.post(`/groups/${row}/chat-attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        })
      ).data;
    },
  });
}

export function useSendGroupMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ row, text, messageType = "text" }) =>
      (await http.post(`/groups/${row}/message`, { text, message_type: messageType })).data,
    onSuccess: (_data, { row }) => {
      qc.invalidateQueries({ queryKey: ["message-log", "all"] });
      if (row) {
        qc.invalidateQueries({ queryKey: ["message-log", "group", row] });
        qc.invalidateQueries({ queryKey: ["group-events", row] });
      }
    },
  });
}


// A short AI summary of the last five back-and-forths in one group. Nothing
// is sent to the group; see apps/api/chat_summary.py.
export function useSummariseGroupChat() {
  return useMutation({
    mutationFn: async (row) => (await http.post(`/groups/${row}/chat-summary`)).data,
  });
}

export function useSendInviteReminder() {
  return useMutation({
    mutationFn: async (row) => (await http.post(`/groups/${row}/invite-reminder`)).data,
  });
}

// Members added to / removed from a group on the desk (stored as group_events).
// The key starts with "group-events" so realtime sync refreshes it too.
export function useMemberChanges(row) {
  return useQuery({
    queryKey: ["group-events", row, "member-changes"],
    queryFn: async () => (await http.get(`/groups/${row}/member-changes`)).data,
    enabled: !!row,
    staleTime: LIVE_STALE_MS,
    refetchOnWindowFocus: false,
  });
}

export function useAddGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ row, name, number }) =>
      (await http.post(`/groups/${row}/members`, { name, number })).data,
    // Only this group's events: the bare prefix refetched every conversation card's.
    onSuccess: (_data, { row }) =>
      qc.invalidateQueries({ queryKey: ["group-events"], predicate: (q) => String(q.queryKey[1]) === String(row) }),
  });
}

export function useRemoveGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ row, key, name, number }) =>
      (await http.post(`/groups/${row}/members/remove`, { key, name, number })).data,
    // Only this group's events: the bare prefix refetched every conversation card's.
    onSuccess: (_data, { row }) =>
      qc.invalidateQueries({ queryKey: ["group-events"], predicate: (q) => String(q.queryKey[1]) === String(row) }),
  });
}

// ---------------------------------------------------------------------------
// Tenant terminology
// ---------------------------------------------------------------------------

// Every signed-in user reads its own brand's wording. Terminology changes
// rarely, so this is cached for the session rather than polled.
export function useTenantConfig(enabled = true) {
  return useQuery({
    queryKey: ["tenant-config"],
    queryFn: async () => (await http.get("/tenant-config")).data,
    enabled,
    staleTime: Infinity,
    retry: false,
  });
}

export function useTenants() {
  return useQuery({
    queryKey: ["superadmin", "tenants"],
    queryFn: async () => (await http.get("/superadmin/tenants")).data,
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await http.post("/superadmin/tenants", payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["superadmin", "tenants"] }),
  });
}

// Renames a brand and/or points it at a different Master email.
export function useUpdateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }) =>
      (await http.patch(`/superadmin/tenants/${id}`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["superadmin", "tenants"] }),
  });
}

export function useUploadTenantLogo(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file) => {
      const body = new FormData();
      body.append("file", file);
      return (await http.post(`/superadmin/tenants/${tenantId}/logo`, body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["superadmin", "tenants"] });
      qc.invalidateQueries({ queryKey: ["tenant-config"] });
    },
  });
}

export function useUploadTenantSidebarImage(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file) => {
      const body = new FormData();
      body.append("file", file);
      return (await http.post(`/superadmin/tenants/${tenantId}/sidebar-image`, body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["superadmin", "tenants"] });
      qc.invalidateQueries({ queryKey: ["tenant-config"] });
    },
  });
}

// --- Vehicle catalog ---------------------------------------------------------

// Active models for the signed-in user's brand (a Super Admin names the brand).
// Rarely changes, so it is cached long: the booking dropdown and every vehicle
// avatar read it.
export function useVehicleCatalog(tenantId) {
  return useQuery({
    queryKey: ["vehicle-catalog", tenantId ?? "mine"],
    queryFn: async () =>
      (await http.get("/vehicle-catalog", { params: tenantId ? { tenant_id: tenantId } : {} })).data,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useSuperadminVehicles(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "vehicles", tenantId],
    queryFn: async () => (await http.get(`/superadmin/tenants/${tenantId}/vehicles`)).data,
    enabled: !!tenantId,
    staleTime: 0,
  });
}

function invalidateVehicles(qc, tenantId) {
  qc.invalidateQueries({ queryKey: ["superadmin", "vehicles", tenantId] });
  qc.invalidateQueries({ queryKey: ["vehicle-catalog"] });
}

export function useCreateVehicle(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await http.post(`/superadmin/tenants/${tenantId}/vehicles`, payload)).data,
    onSuccess: () => invalidateVehicles(qc, tenantId),
  });
}

export function useUpdateVehicle(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }) => (await http.patch(`/superadmin/vehicles/${id}`, payload)).data,
    onSuccess: () => invalidateVehicles(qc, tenantId),
  });
}

export function useDeleteVehicle(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await http.delete(`/superadmin/vehicles/${id}`)).data,
    onSuccess: () => invalidateVehicles(qc, tenantId),
  });
}

export function useUploadVehicleImage(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }) => {
      const body = new FormData();
      body.append("file", file);
      return (await http.post(`/superadmin/vehicles/${id}/image`, body)).data;
    },
    onSuccess: () => invalidateVehicles(qc, tenantId),
  });
}

export function useUnmappedVehicles(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "vehicles-unmapped", tenantId],
    queryFn: async () => (await http.get(`/superadmin/tenants/${tenantId}/vehicles/unmapped`)).data,
    enabled: !!tenantId,
    staleTime: 0,
  });
}

export function useDismissUnmappedVehicle(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await http.delete(`/superadmin/tenants/${tenantId}/vehicles/unmapped/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["superadmin", "vehicles-unmapped", tenantId] }),
  });
}

export function useTenantChat360(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "chat360", tenantId],
    queryFn: async () => (await http.get(`/superadmin/tenants/${tenantId}/chat360`)).data,
    enabled: !!tenantId,
  });
}

export function useSaveTenantChat360(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (values) =>
      (await http.put(`/superadmin/tenants/${tenantId}/chat360`, values)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["superadmin", "chat360", tenantId] }),
  });
}

export function useTestTenantChat360(tenantId) {
  return useMutation({
    mutationFn: async () => (await http.post(`/superadmin/tenants/${tenantId}/chat360/test`)).data,
  });
}

// The brand's fallback Chat360 account: used to make a group when the main number is refused.
const fallbackKey = (tenantId) => ["superadmin", "chat360-fallback", tenantId];

export function useTenantChat360Fallback(tenantId) {
  return useQuery({
    queryKey: fallbackKey(tenantId),
    queryFn: async () => (await http.get(`/superadmin/tenants/${tenantId}/chat360/fallback`)).data,
    enabled: !!tenantId,
  });
}

export function useSaveTenantChat360Fallback(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (values) => (await http.put(`/superadmin/tenants/${tenantId}/chat360/fallback`, values)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: fallbackKey(tenantId) }),
  });
}

export function useRemoveTenantChat360Fallback(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await http.delete(`/superadmin/tenants/${tenantId}/chat360/fallback`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: fallbackKey(tenantId) }),
  });
}

export function useTestTenantChat360Fallback(tenantId) {
  return useMutation({
    mutationFn: async () => (await http.post(`/superadmin/tenants/${tenantId}/chat360/fallback/test`)).data,
  });
}

export function useTenantJourneyStages(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "journey-stages", tenantId],
    queryFn: async () =>
      (await http.get(`/superadmin/tenants/${tenantId}/journey-stages`)).data,
    enabled: !!tenantId,
  });
}

export function useSaveTenantJourneyStages(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (stages) =>
      (await http.put(`/superadmin/tenants/${tenantId}/journey-stages`, { stages })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["superadmin", "journey-stages", tenantId] });
      qc.invalidateQueries({ queryKey: ["superadmin", "dealer-template-options"] });
      qc.invalidateQueries({ queryKey: ["superadmin", "dealer-template-assignments", tenantId] });
      qc.invalidateQueries({ queryKey: ["tenant-config"] });
    },
  });
}

export function useTenantAnalyticsConfig(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "analytics-config", tenantId],
    queryFn: async () =>
      (await http.get(`/superadmin/tenants/${tenantId}/analytics-config`)).data,
    enabled: !!tenantId,
  });
}

export function useSaveTenantAnalyticsConfig(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch) =>
      (await http.put(`/superadmin/tenants/${tenantId}/analytics-config`, patch)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["superadmin", "analytics-config", tenantId] });
      qc.invalidateQueries({ queryKey: ["analytics", "workshop", "volume"] });
      qc.invalidateQueries({ queryKey: ["analytics", "workshop", "response_delays"] });
    },
  });
}

// Like the organization hooks: a tenant id for Super Admin, none for the
// signed-in brand administering its own roles.
const rolesKey = (tenantId) => (tenantId ? ["superadmin", "roles", tenantId] : ["organization", "roles"]);
const rolesBase = (tenantId) => (tenantId ? `/superadmin/tenants/${tenantId}/roles` : "/organization/roles");

export function useTenantRoles(tenantId) {
  return useQuery({
    queryKey: rolesKey(tenantId),
    queryFn: async () => (await http.get(rolesBase(tenantId))).data,
  });
}

// Create (no id), update (id) or delete ({ id, remove: true }) one role.
export function useSaveTenantRole(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, remove, ...role }) => {
      const base = rolesBase(tenantId);
      if (remove) return (await http.delete(`${base}/${id}`)).data;
      if (id) return (await http.put(`${base}/${id}`, role)).data;
      return (await http.post(base, role)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rolesKey(tenantId) });
      qc.invalidateQueries({ queryKey: ["tenant-config"] });
    },
  });
}

// Regions, dealers, workshops and people in one snapshot — every
// Organization page cross-references the others.
//
// `tenantId` is the brand Super Admin is administering. Omit it and the same
// pages run for the signed-in brand instead, narrowed by the API to whatever
// the login's own access policy reaches.
const orgKey = (tenantId) => (tenantId ? ["superadmin", "organization", tenantId] : ["organization", "own"]);
const orgBase = (tenantId) => (tenantId ? `/superadmin/tenants/${tenantId}` : "/organization");

export function useTenantOrganization(tenantId, { enabled = true } = {}) {
  return useQuery({
    queryKey: orgKey(tenantId),
    queryFn: async () =>
      (await http.get(tenantId ? `/superadmin/tenants/${tenantId}/organization` : "/organization")).data,
    enabled,
  });
}

// One write against the organization. `kind` is regions | dealers | workshops
// | people; pass `remove` to delete, `patch` to change in place, otherwise it
// creates.
export function useSaveTenantOrganization(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ kind, id, remove, patch, put, ...values }) => {
      const base = `${orgBase(tenantId)}/${kind}`;
      if (remove) return (await http.delete(`${base}/${id}`)).data;
      if (patch) return (await http.patch(`${base}/${id}`, values)).data;
      if (put) return (await http.put(`${base}/${id}`, values)).data;
      return (await http.post(base, values)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgKey(tenantId) });
      // The workshop's people come from the same app_users rows.
      qc.invalidateQueries({ queryKey: ["stakeholders"] });
      qc.invalidateQueries({ queryKey: ["config"] });
      // Book a Service fills its advisor dropdown from this list, and it is
      // cached indefinitely, so a person added here would never show up there.
      qc.invalidateQueries({ queryKey: ["workshop-service-advisors"] });
    },
  });
}

export function useWaTemplateCatalog() {
  return useQuery({
    queryKey: ["superadmin", "wa-template-catalog"],
    queryFn: async () => (await http.get("/wa-template-catalog")).data,
    staleTime: Infinity,
  });
}

export function useTenantWaTemplates(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "wa-templates", tenantId],
    queryFn: async () => (await http.get(`/superadmin/tenants/${tenantId}/wa-templates`)).data,
    enabled: !!tenantId,
  });
}

export function useSaveTenantWaTemplates(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (templates) =>
      (await http.put(`/superadmin/tenants/${tenantId}/wa-templates`, { templates })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["superadmin", "wa-templates", tenantId] }),
  });
}

// Unified per-brand message config: one row per catalog key, carrying mode
// (Chat360 template vs direct text), name/body, whether the send is enabled,
// and per-key config. Both the Journey editor's template popup and the
// Templates tab render from this.
export function useTenantTemplates(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "templates", tenantId],
    queryFn: async () => (await http.get(`/superadmin/tenants/${tenantId}/templates`)).data,
    enabled: !!tenantId,
  });
}

export function useSaveTenantTemplates(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (templates) =>
      (await http.put(`/superadmin/tenants/${tenantId}/templates`, { templates })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["superadmin", "templates", tenantId] }),
  });
}

export function useTenantSlaLadder(tenantId, ladder = "response") {
  return useQuery({
    queryKey: ["superadmin", "sla-ladder", tenantId, ladder],
    queryFn: async () =>
      (await http.get(`/superadmin/tenants/${tenantId}/sla-ladder`, { params: { ladder } })).data,
    enabled: !!tenantId,
  });
}

export function useSaveTenantSlaLadder(tenantId, ladder = "response") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) =>
      (
        await http.put(`/superadmin/tenants/${tenantId}/sla-ladder`, payload, {
          params: { ladder },
        })
      ).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["superadmin", "sla-ladder", tenantId, ladder] }),
  });
}

// Pre-IFR Journey: where the events to Tata go, and the events most recently sent.
export function useTataIntegration(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "tata-integration", tenantId],
    queryFn: async () => (await http.get(`/superadmin/tenants/${tenantId}/integrations/tata`)).data,
    enabled: !!tenantId,
    refetchInterval: 15000,
  });
}

export function useSaveTataIntegration(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await http.put(`/superadmin/tenants/${tenantId}/integrations/tata`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["superadmin", "tata-integration", tenantId] }),
  });
}

export function useRetryTataEvent(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (eventId) =>
      (await http.post(`/superadmin/tenants/${tenantId}/integrations/tata/events/${eventId}/retry`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["superadmin", "tata-integration", tenantId] }),
  });
}

// The Pre-IFR Agent page: job cards received from the client, and one job card with its timeline.
export function usePreIfrJobCards(params = {}) {
  return useQuery({
    queryKey: ["pre-ifr", "job-cards", params],
    queryFn: async () => (await http.get("/pre-ifr/job-cards", { params })).data,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
}

export function usePreIfrJobCard(row) {
  return useQuery({
    queryKey: ["pre-ifr", "job-card", row],
    queryFn: async () => (await http.get(`/pre-ifr/job-cards/${row}`)).data,
    enabled: !!row,
    refetchOnWindowFocus: false,
  });
}

// Pre-IFR Journey: the workshop whose people the escalation steps reach, and the brand's workshops.
export function usePreIfrSettings(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "pre-ifr-settings", tenantId],
    queryFn: async () => (await http.get(`/superadmin/tenants/${tenantId}/journeys/pre-ifr`)).data,
    enabled: !!tenantId,
  });
}

export function useSavePreIfrSettings(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await http.put(`/superadmin/tenants/${tenantId}/journeys/pre-ifr`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["superadmin", "pre-ifr-settings", tenantId] }),
  });
}

export function useStaffToneFlags(row) {
  return useQuery({
    queryKey: ["staff-tone-flags", row],
    queryFn: async () => (await http.get(`/groups/${row}/staff-tone-flags`)).data,
    enabled: !!row,
    // refetchInterval: REALTIME_FALLBACK_POLL_MS,
  });
}

export function useReviewStaffToneFlag(row) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (flagId) =>
      (await http.post(`/groups/${row}/staff-tone-flags/${flagId}/review`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff-tone-flags", row] }),
  });
}

// Complaints in one group, each with stage history and linked messages, plus
// the brand's lifecycle timings and whether this user may move them.
export function useGroupComplaints(row) {
  return useQuery({
    queryKey: ["complaints", row],
    queryFn: async () => (await http.get(`/groups/${row}/complaints`)).data,
    enabled: !!row,
    // refetchInterval: REALTIME_FALLBACK_POLL_MS,
  });
}

export function useTransitionComplaint(row) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ complaintId, toStage, note = "" }) =>
      (await http.post(`/groups/${row}/complaints/${complaintId}/transition`, { to_stage: toStage, note })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["complaints", row] }),
  });
}

export function useWelcomeMessage(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "welcome-message", tenantId],
    queryFn: async () => (await http.get(`/superadmin/tenants/${tenantId}/welcome-message`)).data,
    enabled: !!tenantId,
  });
}

export function useSaveWelcomeMessage(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      (await http.put(`/superadmin/tenants/${tenantId}/welcome-message`, { body })).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["superadmin", "welcome-message", tenantId] }),
  });
}

export function useWorkshopContactsMessage(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "workshop-contacts-message", tenantId],
    queryFn: async () =>
      (await http.get(`/superadmin/tenants/${tenantId}/workshop-contacts-message`)).data,
    enabled: !!tenantId,
  });
}

export function useSaveWorkshopContactsMessage(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) =>
      (await http.put(`/superadmin/tenants/${tenantId}/workshop-contacts-message`, payload)).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["superadmin", "workshop-contacts-message", tenantId] }),
  });
}

export function useSendWorkshopContacts() {
  return useMutation({
    mutationFn: async ({ row }) =>
      (await http.post(`/groups/${row}/workshop-contacts/send`)).data,
  });
}

export function useDeliveryMessage(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "delivery-message", tenantId],
    queryFn: async () => (await http.get(`/superadmin/tenants/${tenantId}/delivery-message`)).data,
    enabled: !!tenantId,
  });
}

export function useSaveDeliveryMessage(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) =>
      (await http.put(`/superadmin/tenants/${tenantId}/delivery-message`, payload)).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["superadmin", "delivery-message", tenantId] }),
  });
}

export function useNextServiceDueMessage(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "next-service-due-message", tenantId],
    queryFn: async () =>
      (await http.get(`/superadmin/tenants/${tenantId}/next-service-due-message`)).data,
    enabled: !!tenantId,
  });
}

export function useSaveNextServiceDueMessage(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) =>
      (await http.put(`/superadmin/tenants/${tenantId}/next-service-due-message`, payload)).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["superadmin", "next-service-due-message", tenantId] }),
  });
}

export function useSendNextServiceDue() {
  return useMutation({
    mutationFn: async ({ row }) =>
      (await http.post(`/groups/${row}/next-service-due/send`)).data,
  });
}

// Sends a stage the brand added in Super Admin > Service journey, with its file when it takes one.
export function useSendCustomStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ row, stageKey, files }) => {
      const fd = new FormData();
      (files || []).forEach((f) => fd.append("files", f));
      return (await http.post(`/groups/${row}/custom-stages/${stageKey}/send`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["groups"] }),
  });
}

export function useSendPreDeliveryFeedback() {
  return useMutation({
    mutationFn: async ({ row }) =>
      (await http.post(`/groups/${row}/pre-delivery-feedback/send`)).data,
  });
}

export function useSendPostDeliveryFeedback() {
  return useMutation({
    mutationFn: async ({ row }) =>
      (await http.post(`/groups/${row}/post-delivery-feedback/send`)).data,
  });
}

export function usePostDeliveryFeedbackMessage(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "post-delivery-feedback-message", tenantId],
    queryFn: async () =>
      (await http.get(`/superadmin/tenants/${tenantId}/post-delivery-feedback-message`)).data,
    enabled: !!tenantId,
  });
}

export function useSavePostDeliveryFeedbackMessage(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) =>
      (
        await http.put(`/superadmin/tenants/${tenantId}/post-delivery-feedback-message`, payload)
      ).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["superadmin", "post-delivery-feedback-message", tenantId] }),
  });
}

export function useSendWelcomeMessage() {
  return useMutation({
    mutationFn: async ({ row }) => (await http.post(`/groups/${row}/welcome/send`)).data,
  });
}

export function useApprovePreInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ row }) => (await http.post(`/groups/${row}/pre-invoice/approve`)).data,
    onSuccess: (data) => qc.setQueryData(["groups"], { headers: data.headers, rows: data.rows }),
  });
}

export function useMarkInvoiceSent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ row }) => (await http.post(`/groups/${row}/invoice/mark-sent`)).data,
    onSuccess: (data) => qc.setQueryData(["groups"], { headers: data.headers, rows: data.rows }),
  });
}

export function useInitialMessageConfig(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "initial-message-config", tenantId],
    queryFn: async () =>
      (await http.get(`/superadmin/tenants/${tenantId}/initial-message-config`)).data,
    enabled: !!tenantId,
  });
}

export function useSaveInitialMessageConfig(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (enabled) =>
      (await http.put(`/superadmin/tenants/${tenantId}/initial-message-config`, { enabled })).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["superadmin", "initial-message-config", tenantId] }),
  });
}

export function useComplaintConfig(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "complaint-config", tenantId],
    queryFn: async () => (await http.get(`/superadmin/tenants/${tenantId}/complaint-config`)).data,
    enabled: !!tenantId,
  });
}

export function useSaveComplaintConfig(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (config) =>
      (await http.put(`/superadmin/tenants/${tenantId}/complaint-config`, config)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["superadmin", "complaint-config", tenantId] }),
  });
}

export function useCostRates(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "cost-rates", tenantId],
    queryFn: async () => (await http.get(`/superadmin/tenants/${tenantId}/cost-rates`)).data,
    enabled: !!tenantId,
  });
}

export function useSaveCostRates(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rates) =>
      (await http.put(`/superadmin/tenants/${tenantId}/cost-rates`, rates)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["superadmin", "cost-rates", tenantId] });
      // The estimate is derived from the rate card, so it is stale the moment
      // the card changes.
      qc.invalidateQueries({ queryKey: ["superadmin", "cost-estimate", tenantId] });
    },
  });
}

// from/to are YYYY-MM-DD and both inclusive — the window of real sends to price.
export function useCostEstimate(tenantId, { from, to } = {}) {
  return useQuery({
    queryKey: ["superadmin", "cost-estimate", tenantId, from || "", to || ""],
    queryFn: async () =>
      (
        await http.get(`/superadmin/tenants/${tenantId}/cost-estimate`, {
          params: { from_date: from || undefined, to_date: to || undefined },
        })
      ).data,
    enabled: !!tenantId,
    // Keep the previous window's numbers on screen while a new one loads.
    placeholderData: (previous) => previous,
  });
}

// from/to are YYYY-MM-DD and both inclusive — the window of real Claude
// classifier calls to price. Separate query from useCostEstimate since it
// hits its own endpoint and has its own date range in the UI.
export function useLlmCostEstimate(tenantId, { from, to } = {}) {
  return useQuery({
    queryKey: ["superadmin", "llm-cost-estimate", tenantId, from || "", to || ""],
    queryFn: async () =>
      (
        await http.get(`/superadmin/tenants/${tenantId}/llm-cost-estimate`, {
          params: { from_date: from || undefined, to_date: to || undefined },
        })
      ).data,
    enabled: !!tenantId,
    placeholderData: (previous) => previous,
  });
}

// System health. `scope` is { tenantId, locations, from, to }, every part optional:
// - tenantId: the brand, for the Super Admin (empty = every brand). Anyone else is
//   kept to their own brand by the API, which ignores it.
// - locations: comma-separated workshops to narrow to (empty = all the login may see).
// - from/to: YYYY-MM-DD, both inclusive; the API defaults to the last 7 days.
// Overview and Server cover the whole platform, so only the Super Admin can read them.
const systemHealth = (path, params, { admin = false } = {}) => async () =>
  (await http.get(`${admin ? "/superadmin" : ""}/system-health/${path}`, { params })).data;

const healthParams = ({ tenantId, locations, from, to } = {}) => ({
  tenant_id: tenantId || undefined,
  locations: locations || undefined,
  from_date: from || undefined,
  to_date: to || undefined,
});

const healthKey = (name, { tenantId, locations, from, to } = {}, ...rest) => [
  "system-health", name, tenantId || "", locations || "", from || "", to || "", ...rest,
];

// A health page that never refreshes would keep showing a problem long after it was fixed.
const HEALTH_STALE_MS = 30000;

const useHealthQuery = (name, path, scope, extra = {}, keyRest = [], options = {}) =>
  useQuery({
    queryKey: healthKey(name, scope, ...keyRest),
    queryFn: systemHealth(path, { ...healthParams(scope), ...extra }, options),
    staleTime: HEALTH_STALE_MS,
    placeholderData: (previous) => previous,
    ...(options.admin ? { refetchInterval: 60000 } : {}),
  });

// Every open problem from the other tabs in one list.
export const useSystemHealthProblems = (scope) => useHealthQuery("problems", "problems", scope, {}, [], { admin: true });

// The server and our four programs. The whole server is one thing: no brand filter.
export const useSystemHealthMachine = ({ from, to } = {}) =>
  useHealthQuery("machine", "machine", { from, to }, {}, [], { admin: true });

export const useSystemHealthJourney = (scope) => useHealthQuery("journey", "journey", scope);

// The Pre-IFR Agent's log: what Tata and the bot sent us, the templates and events we sent, and each
// job card's own activity. kind "" asks for every kind.
export function useSystemHealthPreIfr({ kind, search, problems, limit = 50, offset = 0, ...scope } = {}) {
  return useHealthQuery(
    "pre-ifr", "pre-ifr", scope,
    { kind: kind || undefined, search: search || undefined, problems: problems ? true : undefined, limit, offset },
    [kind || "", search || "", problems ? "problems" : "", limit, offset],
  );
}
export const useSystemHealthGroups = (scope) => useHealthQuery("groups", "groups", scope);
export const useSystemHealthJobs = (scope) => useHealthQuery("jobs", "jobs", scope);
export const useSystemHealthEscalations = (scope) => useHealthQuery("escalations", "escalations", scope);

// stage "" asks for sends not linked to a stage; undefined asks for every stage.
export function useSystemHealthSends({ stage, template, result, errorCode, limit = 50, offset = 0, ...scope } = {}) {
  return useHealthQuery(
    "sends", "sends", scope,
    { stage, template, result: result || undefined, error_code: errorCode || undefined, limit, offset },
    [stage ?? "*", template ?? "*", result || "", errorCode || "", limit, offset],
  );
}

// step + state: the bookings whose step is in that state. A step alone: those
// that failed or got stuck there.
export function useSystemHealthGroupBookings({ outcome, reason, step, state, attention, limit = 50, offset = 0, ...scope } = {}) {
  return useHealthQuery(
    "group-bookings", "group-bookings", scope,
    {
      outcome: outcome || undefined, reason: reason || undefined, step: step || undefined,
      state: state || undefined, attention: attention ? true : undefined, limit, offset,
    },
    [outcome || "", reason || "", step || "", state || "", attention ? "attention" : "", limit, offset],
  );
}

// state: one job state; attention: only the jobs that failed, are late or got stuck.
export function useSystemHealthJobList({ state, jobType, stage, attention, limit = 50, offset = 0, ...scope } = {}) {
  return useHealthQuery(
    "job-list", "job-list", scope,
    {
      state: state || undefined, job_type: jobType || undefined, stage: stage || undefined,
      attention: attention ? true : undefined, limit, offset,
    },
    [state || "", jobType || "", stage || "", attention ? "attention" : "", limit, offset],
  );
}

// One record in full. The API answers "not found" for one outside the login's reach.
const useHealthRecord = (name, path, id) =>
  useQuery({
    queryKey: ["system-health", name, id],
    queryFn: systemHealth(path, {}),
    staleTime: HEALTH_STALE_MS,
    enabled: id != null,
  });

export const useSystemHealthSend = (id) => useHealthRecord("send", `sends/${id}`, id);
export const useSystemHealthGroupBooking = (id) => useHealthRecord("group-booking", `group-bookings/${id}`, id);

export function useFeatureFlags(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "feature-flags", tenantId],
    queryFn: async () =>
      (await http.get(`/superadmin/tenants/${tenantId}/feature-flags`)).data,
    enabled: !!tenantId,
  });
}

export function useSaveFeatureFlags(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (flags) =>
      (await http.put(`/superadmin/tenants/${tenantId}/feature-flags`, flags)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["superadmin", "feature-flags", tenantId] });
      // Turning the stage engine on saves the starter journey into the brand's stages.
      qc.invalidateQueries({ queryKey: ["superadmin", "journey-stages", tenantId] });
    },
  });
}

// Workshop Analytics — every figure is aggregated server-side (apps/api/analytics.py).
// `params` is { from, to, locations?, bucket? | by? }; passing null keeps the query idle,
// and each panel's own toggle lives in its params so panels refetch independently.
function useWorkshopAnalytics(metric, params) {
  return useQuery({
    queryKey: ["analytics", "workshop", metric, params],
    queryFn: async () => (await http.get(`/analytics/workshop/${metric}`, { params })).data,
    enabled: !!params?.from && !!params?.to,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useWorkshopVolume(params) {
  return useWorkshopAnalytics("volume", params);
}

// Separate metric from useWorkshopVolume: this counts groups.created_at,
// /volume counts the job_card_sent event -- different clocks, not two
// views of one.
export function useWorkshopGroupsCreated(params) {
  return useWorkshopAnalytics("groups-created", params);
}

// Per-car figures behind the redesigned analytics cards: customers not joined,
// messages waiting, escalation reasons, NPS, delivery feedback, top customers.
export function useWorkshopAttention(params) {
  return useWorkshopAnalytics("attention", params);
}

// Job cards and pick & drop choices per workshop, for the pick & drop card.
export function useWorkshopPickDrop(params) {
  return useWorkshopAnalytics("pickup-drop", params);
}

// Bodyshop groups created, job cards sent, cars waiting and delivered, per workshop and day.
export function useWorkshopBodyshop(params) {
  return useWorkshopAnalytics("bodyshop", params);
}

// Cars repaired or delivered per day ('repaired' | 'delivered'), for the flow card's chart.
export function useWorkshopEventVolume(event, params) {
  return useWorkshopAnalytics("event-volume", params && { ...params, event });
}

export function useWorkshopTat(params) {
  return useWorkshopAnalytics("tat", params);
}

export function useWorkshopCycleTime(params) {
  return useWorkshopAnalytics("cycle-time", params);
}

export function useResponseDelays(params) {
  return useWorkshopAnalytics("response-delays", params);
}

export function useEscalationRate(params) {
  return useWorkshopAnalytics("escalation-rate", params);
}

// The escalated groups behind one row of the escalation-rate table -
// `params` is { from, to, by, key, ladder? , locations? }.
export function useEscalationRateDetail(params) {
  return useQuery({
    queryKey: ["analytics", "workshop", "escalation-rate-detail", params],
    queryFn: async () => (await http.get("/analytics/workshop/escalation-rate/detail", { params })).data,
    enabled: !!params?.from && !!params?.to && !!params?.key,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

// WhatsApp templates made from Super Admin. The list refreshes itself every 30 s while any
// template is still waiting for Meta, so a decision shows up without a reload.
export function useWaTemplates(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "wa-templates", tenantId],
    queryFn: async () => (await http.get("/superadmin/wa-templates", { params: { tenant_id: tenantId } })).data.rows,
    enabled: !!tenantId,
    refetchInterval: (query) => ((query.state.data || []).some((r) => r.status === "pending") ? 30_000 : false),
  });
}

export function useCreateWaTemplate(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (template) => (await http.post("/superadmin/wa-templates", { ...template, tenant_id: tenantId })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["superadmin", "wa-templates", tenantId] }),
  });
}

// Templates already on the brand's Chat360 number whose name contains `q`, to add to the brand.
export function useSearchChat360Templates(tenantId, q) {
  const term = (q || "").trim();
  return useQuery({
    queryKey: ["superadmin", "wa-templates", tenantId, "chat360-search", term],
    queryFn: async () => (await http.get("/superadmin/wa-templates/chat360-search", { params: { tenant_id: tenantId, q: term } })).data.rows,
    enabled: !!tenantId && term.length >= 2,
    staleTime: 15_000,
    retry: false,
  });
}

// Adds a template that is already on Chat360, by its exact name. Nothing is made or sent to Meta.
export function useAddExistingWaTemplate(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name) => (await http.post("/superadmin/wa-templates/existing", { tenant_id: tenantId, name })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["superadmin", "wa-templates", tenantId] }),
  });
}

// The message steps a template can be given to: the built-in ones, and the brand's own stages.
export function useDealerTemplateOptions(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "dealer-template-options", tenantId ?? null],
    queryFn: async () => (await http.get("/superadmin/dealer-template-options", { params: tenantId ? { tenant_id: tenantId } : {} })).data,
    staleTime: 60_000,
  });
}

export function useDealerTemplateAssignments(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "dealer-template-assignments", tenantId],
    queryFn: async () => (await http.get("/superadmin/dealer-template-assignments", { params: { tenant_id: tenantId } })).data.rows,
    enabled: !!tenantId,
  });
}

export function useAssignDealerTemplate(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, template_key, field_map }) => (await http.put(`/superadmin/wa-templates/${id}/assignment`, { template_key, field_map })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["superadmin", "dealer-template-assignments", tenantId] });
      // An approved brand template also becomes the step's template name.
      qc.invalidateQueries({ queryKey: ["superadmin", "templates", tenantId] });
    },
  });
}

export function useRemoveDealerAssignment(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await http.delete(`/superadmin/dealer-template-assignments/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["superadmin", "dealer-template-assignments", tenantId] }),
  });
}

// Which field fills each {{n}} of a template: kept with the template, used by every stage that picks it.
export function useSetWaTemplateFields(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, field_map }) => (await http.put(`/superadmin/wa-templates/${id}/fields`, { field_map })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["superadmin", "wa-templates", tenantId] }),
  });
}

export function useDeleteWaTemplate(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await http.delete(`/superadmin/wa-templates/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["superadmin", "wa-templates", tenantId] }),
  });
}

// The brand's current WhatsApp number, and which templates are not on it yet.
export function useWaTemplateMoveOptions(tenantId) {
  return useQuery({
    queryKey: ["superadmin", "wa-templates", tenantId, "move"],
    queryFn: async () => (await http.get("/superadmin/wa-templates/move-options", { params: { tenant_id: tenantId } })).data,
    enabled: !!tenantId,
    retry: false,
  });
}

// Builds up to ten templates again on the brand's current number.
export function useMoveWaTemplates(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids) => (await http.post("/superadmin/wa-templates/move", { tenant_id: tenantId, ids })).data,
    onSettled: () => qc.invalidateQueries({ queryKey: ["superadmin", "wa-templates", tenantId] }),
  });
}

// The sample file of an image, video or document template; gives back its public link.
export function useUploadWaTemplateSample() {
  return useMutation({
    mutationFn: async ({ kind, file }) => {
      const body = new FormData();
      body.append("file", file);
      return (await http.post("/superadmin/wa-templates/sample-file", body, { params: { kind } })).data;
    },
  });
}

export function useRefreshWaTemplate(tenantId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await http.post(`/superadmin/wa-templates/${id}/refresh`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["superadmin", "wa-templates", tenantId] }),
  });
}
